import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import type {
  CartBrandDiscount,
  Nullable,
  ProductKind,
} from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import compact from 'lodash-es/compact';
import get from 'lodash-es/get';
import uniq from 'lodash-es/uniq';

import type { AlgoliaProduct } from '@jane/search/types';
import { cartSize } from '@jane/shared-ecomm/util';
// eslint-disable-next-line no-restricted-imports
import type { BrandSpecial as tBrandSpecial } from '@jane/shared/models';
import type {
  BaseStore,
  BrandSpecial,
  Cart,
  EmptyCart,
  Kiosk,
  MaterializedMenuProduct,
  MenuProduct,
  PendingCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  statePostalCode,
} from '@jane/shared/util';

import type { MenuCategory } from '../../../types';
import { convertTimeoutToString } from '../convertTimeoutToString/convertTimeoutToString';
import { hasSpecial } from '../specials/hasSpecial';
import type { CheckoutType } from './Properties';

interface BuildCheckoutTypeArgs {
  anonymousCheckout?: boolean;
  separateDeviceCheckout?: Nullable<boolean>;
}

interface BuildDealArgs {
  specialIds: (number | undefined | null)[];
  specials?: (StoreSpecial | V3StoreSpecial)[];
}

interface BuildProductPropertiesArgs {
  product:
    | AlgoliaProduct
    | MenuProduct
    | PendingCartProduct
    | MaterializedMenuProduct;
  productPercentageOff: Nullable<number>;
  productSavingsAmount: Nullable<number>;
}

export const buildCartProperties = (cart: Cart | EmptyCart) => {
  const {
    all_discounts_total,
    brand_discounts: brandDiscounts,
    brand_discounts_service_fee_total,
    brand_discounts_total,
    id,
    item_total,
    order_total,
    sales_tax_amount,
    service_fee_amount,
    store_tax_amount,
    products,
  } = cart;
  const totalTaxAmount = (sales_tax_amount ?? 0) + (store_tax_amount ?? 0);
  const hasDiscounts = hasSpecial(products);
  const hasBrandDiscounts = brandDiscounts?.length > 0;
  const brandSpecialIds = hasBrandDiscounts
    ? brandDiscounts?.map(({ brand_special_id }) => brand_special_id)
    : null;

  const discountPercentage = hasDiscounts
    ? `${((Number(all_discounts_total) / Number(item_total)) * 100).toFixed(
        2
      )}%`
    : null;

  return {
    'Brand Name in Bag': uniq(compact(products.map(({ brand }) => brand))),
    'Cart ID': String(id),
    'Deal?': hasDiscounts,
    'Estimated Service Fees': service_fee_amount ?? 0,
    'Estimated Subtotal': item_total ?? 0,
    'Estimated Taxes': totalTaxAmount ?? 0,
    'Estimated Total': order_total ?? 0,
    'Product Category in Bag': uniq(compact(products.map(({ kind }) => kind))),
    'Product ID in Bag': products.map(({ id }) => id),
    'Total Percentage Off': discountPercentage,
    'Total Product Count': cartSize(cart),
    'Total Savings Amount': all_discounts_total ?? null,
    'Has Brand Discount': hasBrandDiscounts,
    'Brand Discount IDs': hasBrandDiscounts ? brandSpecialIds : null,
    'Brand Discount Service Fee Total': hasBrandDiscounts
      ? brand_discounts_service_fee_total
      : null,
    'Brand Discount Total': brand_discounts_total ?? null,
  };
};

export const buildBagInfoProperties = (
  cart?: Cart | Record<string, never> | null
) => {
  if (!cart || cartSize(cart) === 0) {
    return {
      'Brand ID in Bag': null,
      'Brand Name in Bag': null,
      'Cart ID': null,
      'Deal in bag?': null,
      'Estimated Total in Bag': null,
      'Product Category in Bag': null,
      'Product ID in Bag': null,
      'Product Name in Bag': null,
      'Total Product Count in Bag': null,
    };
  }
  const { id, order_total, products } = cart;

  return {
    'Brand ID in Bag': uniq(
      compact(cart.products.map((p) => p.product_brand_id))
    ),
    'Brand Name in Bag': uniq(compact(products.map(({ brand }) => brand))),
    'Cart ID': String(id),
    'Deal in bag?': hasSpecial(products),
    'Estimated Total in Bag': order_total ?? 0,
    'Product Category in Bag': uniq(compact(products.map(({ kind }) => kind))),
    'Product ID in Bag': products.map(({ id }) => id),
    'Product Name in Bag': uniq(compact(products.map(({ name }) => name))),
    'Total Product Count in Bag': cartSize(cart),
  };
};

export const buildCheckoutType = ({
  anonymousCheckout,
  separateDeviceCheckout,
}: BuildCheckoutTypeArgs) => {
  const checkoutTypes: CheckoutType[] = [
    separateDeviceCheckout ? 'qr code' : 'classic',
  ];
  if (anonymousCheckout) {
    checkoutTypes.push('anonymous');
  }
  return checkoutTypes;
};

export const buildCondensedCartProperties = (cart?: Cart | EmptyCart) => {
  if (!cart || cartSize(cart) === 0) {
    return {
      'Cart ID': '0',
      'Deal?': false,
      'Estimated Total': 0,
      'Total Percentage Off': null,
      'Total Savings Amount': null,
    };
  }
  const { all_discounts_total, id, item_total, order_total, products } = cart;
  const hasDiscounts = hasSpecial(products);
  const discountPercentage = hasDiscounts
    ? `${((Number(all_discounts_total) / Number(item_total)) * 100).toFixed(
        2
      )}%`
    : null;

  return {
    'Cart ID': String(id),
    'Deal?': hasDiscounts,
    'Estimated Total': order_total ?? 0,
    'Total Percentage Off': discountPercentage,
    'Total Savings Amount': all_discounts_total ?? null,
  };
};

export const buildDealType = ({ specialIds, specials }: BuildDealArgs) => {
  const dealType = uniq(
    compact(
      specialIds.map((productSpecialId) =>
        specials
          ?.find(({ id }) => id === productSpecialId)
          ?.special_type.replace('_', ' ')
      )
    )
  );
  return dealType.length > 0 ? dealType : null;
};

export const buildDealNames = ({ specialIds, specials }: BuildDealArgs) => {
  const dealNames = uniq(
    compact(
      specialIds.map(
        (productSpecialId) =>
          specials?.find(({ id }) => id === productSpecialId)?.title
      )
    )
  );
  return dealNames.length > 0 ? dealNames : null;
};

export const buildSessionTimeoutProperties = (timeoutMs: number) => {
  return {
    'Timeout Settings in Internal Tools': convertTimeoutToString(timeoutMs),
    'Timeout Settings in Seconds': String(timeoutMs / 1000),
  };
};

export const buildKioskSettingProperties = (kiosk?: Nullable<Kiosk>) => ({
  'Phone number disabled?': Boolean(kiosk?.disable_phone_number_at_checkout),
  'Pickup notes enabled?': Boolean(kiosk?.show_checkout_message_field),
});

export const buildStoreProperties = (store: Store | BaseStore) => ({
  "Store's Anonymous Checkout Setting": store.kiosk_anonymous_checkout,
  "Store's City": store.city,
  'Store ID': store.id,
  "Store's Lat": `${get(store, 'lat') || get(store, 'latitude')}`,
  "Store's Long": `${get(store, 'long') || get(store, 'longitude')}`,
  'Store Name': store.name,
  "Store's State": statePostalCode(store.state),
  "Store's Zip Code": store.zip,
});

export const buildCategoryPositionProperties = (
  categoryList: MenuCategory[]
) => {
  const categoryPositions = Object.fromEntries(
    categoryList.map((cat, index) => [cat, index + 1])
  );

  return {
    'Default tab': categoryList[0],
    'Edibles tab position': categoryPositions['edible'] ?? null,
    'Extract tab position': categoryPositions['extract'] ?? null,
    'Flower tab position': categoryPositions['flower'] ?? null,
    'Gear tab position': categoryPositions['gear'] ?? null,
    'Merch tab position': categoryPositions['merch'] ?? null,
    'Pre-roll tab position': categoryPositions['pre-roll'] ?? null,
    'Shop all products tab position': categoryPositions['all'] ?? null,
    'Tinctures tab position': categoryPositions['tincture'] ?? null,
    'Topicals tab position': categoryPositions['topical'] ?? null,
    'Total number of menu tabs': categoryList.length,
    'Vapes tab position': categoryPositions['vape'] ?? null,
  };
};
export const buildProductProperties = (
  productDetails: BuildProductPropertiesArgs
) => {
  const { product, productPercentageOff, productSavingsAmount } =
    productDetails;
  const weightCount = availableWeightsForMenuProduct(
    product as AlgoliaProduct
  ).length;

  const brandSpecialIds = product.applicable_brand_special_ids as number[];

  const brandSpecialTypes =
    brandSpecialIds && 'applicable_bundle_brand_special_ids' in product
      ? brandSpecialIds.map((id) =>
          [
            ...(product.applicable_bundle_brand_special_ids?.discount || []),
            ...(product.applicable_bundle_brand_special_ids?.qualify || []),
          ].includes(id)
            ? 'bundle'
            : 'product'
        )
      : undefined;

  const brandSpecialValues = {
    'Brand Discount IDs': brandSpecialIds,
    'Brand Reward Types': brandSpecialTypes,
  };

  return {
    'Product Category': product.kind as ProductKind,
    'Product ID': String(product.product_id),
    'Product Name': product.name,
    'Product Rating':
      product.aggregate_rating && product.aggregate_rating !== 0
        ? product.aggregate_rating
        : null,
    'Total Product Ratings Count': product.review_count ?? null,
    Lineage: String(product.category),
    'Brand Name': String(product.brand),
    'Deal?': !!product?.special_amount,
    'Product Savings Amount': productSavingsAmount,
    'Product Percentage Off': productPercentageOff,
    'Product with multiple weights?': weightCount > 1 ? true : false,
    'Number of weight options': weightCount > 1 ? weightCount : null,
    'Has Brand Discount': !!product.has_brand_discount,
    ...brandSpecialValues,
  };
};

export const getOptionSelected = (
  optionSelected:
    | MenuCategory
    | 'bag icon'
    | 'search icon'
    | 'login icon'
    | 'logout icon'
    | 'menu switcher'
    | 'sign in prompt'
) => {
  switch (optionSelected) {
    case 'all':
      return 'shop all products';
    default:
      return optionSelected;
  }
};

export const buildOrderSubmittedBrandDiscountProperties = (
  brandDiscounts: CartBrandDiscount[]
) => {
  const hasBrandDiscounts = brandDiscounts?.length > 0;
  const brandSpecialTypes = hasBrandDiscounts
    ? brandDiscounts.map(({ special_type }) =>
        special_type === 'product' ? 'product' : 'bundle'
      )
    : null;
  const brandSpecials = hasBrandDiscounts
    ? brandDiscounts.map((special) =>
        [
          special.product_brand.name,
          special.special_title,
          formatCurrency(special.discount_total_amount),
          special.special_type === 'product' ? 'product' : 'bundle',
        ].join(', ')
      )
    : null;

  const productBrandSpecials = hasBrandDiscounts
    ? brandDiscounts.filter((special) => special.special_type === 'product')
    : [];
  const bundleBrandSpecials = hasBrandDiscounts
    ? brandDiscounts.filter((special) => special.special_type !== 'product')
    : [];

  return {
    'Brand Specials': brandSpecials,
    'Brand Reward Type': brandSpecialTypes,
    'Brand Discount Bundle Total':
      bundleBrandSpecials?.length > 0
        ? bundleBrandSpecials
            .map((special) => special.discount_total_amount)
            .reduce((acc, curr) => acc + curr, 0)
        : null,
    'Brand Discount Product Total':
      productBrandSpecials?.length > 0
        ? productBrandSpecials
            .map((special) => special.discount_total_amount)
            .reduce((acc, curr) => acc + curr, 0)
        : null,
  };
};

export const buildBrandSpecialProperties = (
  brandSpecial: BrandSpecial | tBrandSpecial
) => {
  const brandDiscountDetails =
    brandSpecial.discount_type === 'percent'
      ? brandSpecial.discount_percent
      : brandSpecial.discount_type === 'target_price'
        ? brandSpecial.discount_target_price
        : brandSpecial.discount_dollar_amount;
  return {
    'Brand ID': Number(brandSpecial.product_brand?.id),
    'Brand Name': brandSpecial.product_brand?.name,
    'Brand Discount ID': brandSpecial.id,
    'Brand Discount Type': brandSpecial.discount_type.replace('_', ' '),
    'Brand Reward Type':
      brandSpecial.special_type === 'product' ? 'Product' : 'Bundle',
    'Brand Discount Details': brandDiscountDetails,
  };
};
